import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from 'utils/axiosConfig';
import { FAST_API_BASE_URL } from 'constants';
import { useAuth } from 'context/AuthContext';
import { clearAuthStorage, getDomainsFromResponse, isSuperAdmin, getUserRole } from '../utils';
import { useSession } from './useSession';
import { STORAGE_KEYS } from '../constants';

export const useInspection = () => {
  const navigate = useNavigate();
  const { login, logout, user } = useAuth();
  const { initializeSession } = useSession();
  const [isInspectionMode, setIsInspectionMode] = useState(false);
  const [isSwitchingUser, setIsSwitchingUser] = useState(false);
  const [inspectDialogOpen, setInspectDialogOpen] = useState(false);
  const [inspectTargetEmail, setInspectTargetEmail] = useState('');
  const processingRef = useRef(false);
  const hasProcessedRef = useRef(false);

  const performInspectionLogin = useCallback(async (email) => {
    if (processingRef.current) {
      return { success: false, error: 'Already processing' };
    }
    
    processingRef.current = true;
    setIsSwitchingUser(true);
    
    try {
      const response = await axiosInstance.post(`${FAST_API_BASE_URL}/api/user/re-login`, {
        inspect_token: email,
      });
      
      if (response.status === 200 && response.data) {
        const { result } = response.data;
        // sessionStorage.setItem('inspection_mode', 'true');
        // sessionStorage.setItem('inspected_email', email);
        if (result.original_user_id) {
          sessionStorage.setItem('original_user_id', result.original_user_id);
        }
        
        const domains = getDomainsFromResponse(result);
        const userRole = getUserRole(result);
        
        if (userRole) {
          localStorage.setItem(STORAGE_KEYS.ROLE, userRole);
        }
        
        initializeSession(false);
        
        const loginSuccess = login(
          {
            id: result.user_id,
            email: result.email,
            name: result.name || email.split('@')[0],
            profile_pic: result.profile_pic || null,
            is_super_admin: isSuperAdmin(result.is_super_admin),
            is_whitelabel_user: result.is_whitelabel_user,
            is_whitelabelling_reseller_only: result.is_whitelabelling_reseller_only || false,
            is_inspection_mode: true,
            original_user_id: result.original_user_id || user?.id
          },
          domains
        );
        
        if (loginSuccess) {
          setIsInspectionMode(true);
          return { success: true, userData: result };
        }
      }
      
      return { success: false, error: 'Inspection login failed - invalid response' };
    } catch (error) {
      console.error('Inspection login failed:', error);
      let errorMessage = 'Inspection failed. Please try again.';
      
      if (error.response) {
        console.error('Error response:', error.response.data);
        if (error.response.status === 401) {
          errorMessage = 'You do not have permission to inspect this user';
        } else if (error.response.status === 403) {
          errorMessage = 'Inspection access denied';
        } else if (error.response.data?.messages?.[0]) {
          errorMessage = error.response.data.messages[0];
        } else if (error.response.data?.message) {
          errorMessage = error.response.data.message;
        }
      }
      
      return { success: false, error: errorMessage };
    } finally {
      processingRef.current = false;
      setIsSwitchingUser(false);
    }
  }, [login, initializeSession, user]);

  const initiateInspection = useCallback(async (email) => {
    try {
      // Clear any existing session data first
      clearAuthStorage();
      
      // Clear inspection flags
      sessionStorage.removeItem('inspection_mode');
      sessionStorage.removeItem('inspected_email');
      sessionStorage.removeItem('original_user_id');
      
      // Logout current user if any
      await logout();
      
      // Small delay to ensure logout is complete
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Perform inspection login
      const result = await performInspectionLogin(email);
      
      if (result.success) {
        return { success: true };
      } else {
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('Initiate inspection error:', error);
      return { success: false, error: error.message || 'Failed to initiate inspection' };
    }
  }, [logout, performInspectionLogin]);

  const confirmInspection = useCallback(async () => {
    if (!inspectTargetEmail) {
      console.error('No target email for inspection');
      return { success: false, error: 'No target email specified' };
    }
    
    const result = await initiateInspection(inspectTargetEmail);
    
    if (result.success) {
      setInspectDialogOpen(false);
      setInspectTargetEmail('');
      
      // Navigate based on user role after inspection
      setTimeout(() => {
        const isSuperAdminUser = isSuperAdmin(user?.is_super_admin);
        const domains = user?.domains || [];
        const hasDomains = domains && domains.length > 0;
        const isReadOnly = hasDomains && domains[0]?.role === "read only";
        
        if (isSuperAdminUser) {
          navigate("/customers");
        } else if (isReadOnly) {
          navigate("/dashboard");
        } else {
          navigate("/settings");
        }
      }, 500);
      
      return { success: true };
    } else {
      console.error('Switch user failed:', result.error);
      return { success: false, error: result.error };
    }
  }, [inspectTargetEmail, initiateInspection, navigate, user]);

  const cancelInspection = useCallback(() => {
    setInspectDialogOpen(false);
    setInspectTargetEmail('');
    setIsSwitchingUser(false);
    setIsInspectionMode(false);
    hasProcessedRef.current = false;
    
    // Clear inspection flags from storage
    sessionStorage.removeItem('inspection_mode');
    sessionStorage.removeItem('inspected_email');
    sessionStorage.removeItem('original_user_id');
  }, []);

  const openInspectionDialog = useCallback((email) => {
    setInspectTargetEmail(email);
    setInspectDialogOpen(true);
  }, []);

  const handleInspectionFromUrl = useCallback(async (email) => {
    // Prevent double processing
    if (hasProcessedRef.current) {
      console.log('Inspection already processed, skipping...');
      return false;
    }
    
    if (!email) return false;
    
    hasProcessedRef.current = true;
    
    const result = await initiateInspection(email);
        if (result.success) {
      setInspectDialogOpen(false);
      setInspectTargetEmail('');
      console.log(user)
      setTimeout(() => {
        const isSuperAdminUser = isSuperAdmin(user?.is_super_admin);
        const domains = user?.domains || [];
        const hasDomains = domains && domains.length > 0;
        const isReadOnly = hasDomains && domains[0]?.role === "read only";
        
        if (isSuperAdminUser) {
          navigate("/customers");
        } else if (isReadOnly) {
          navigate("/dashboard");
        } else {
          navigate("/settings");
        }
      }, 500);
      
      return true ;
    } else {
      console.error('Switch user failed:', result.error);
      return { success: false, error: result.error };
    }
    
    // if (!result.success) {
    //   hasProcessedRef.current = false;
    //   console.error('Inspection failed:', result.error);
    //   return false;
    // }
    
    // return true;
  }, [initiateInspection]);

  return {
    isInspectionMode,
    isSwitchingUser,
    inspectDialogOpen,
    inspectTargetEmail,
    confirmInspection,
    cancelInspection,
    openInspectionDialog,
    setInspectDialogOpen,
    handleInspectionFromUrl
  };
};